import React from 'react';
import { Info, Lightbulb, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * HelperNote — proje genelinde tek tip kullanılan yumuşak callout.
 * Açıklama/uyarı/ipucu metinlerini görsel olarak ayırır, marka rengiyle
 * (cyan/sky tonları) tutarlı, hafif gradient ve sol akıllı şerit içerir.
 *
 * Variants:
 *   info     (default) — nötr bilgilendirme  (sky / mavi)
 *   tip      — ipucu / öneri              (mor)
 *   warning  — dikkat çek                  (amber)
 *   success  — onay / olumlu              (emerald)
 *   error    — hata / kritik               (kırmızı)
 *
 * Sizes:
 *   sm   (varsayılan) — form helper text
 *   md   — modal / page level callout
 */
const VARIANT_STYLES = {
  info: {
    container: 'bg-gradient-to-r from-sky-50 to-blue-50/40 border-sky-200/60 ring-sky-100',
    accent: 'bg-sky-400',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    text: 'text-sky-900/80',
    Icon: Info,
  },
  tip: {
    container: 'bg-gradient-to-r from-violet-50 to-fuchsia-50/40 border-violet-200/60 ring-violet-100',
    accent: 'bg-violet-400',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    text: 'text-violet-900/80',
    Icon: Lightbulb,
  },
  warning: {
    container: 'bg-gradient-to-r from-amber-50 to-orange-50/40 border-amber-200/60 ring-amber-100',
    accent: 'bg-amber-400',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    text: 'text-amber-900/80',
    Icon: AlertTriangle,
  },
  success: {
    container: 'bg-gradient-to-r from-emerald-50 to-teal-50/40 border-emerald-200/60 ring-emerald-100',
    accent: 'bg-emerald-400',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    text: 'text-emerald-900/80',
    Icon: CheckCircle2,
  },
  error: {
    container: 'bg-gradient-to-r from-rose-50 to-red-50/40 border-rose-200/60 ring-rose-100',
    accent: 'bg-rose-400',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    text: 'text-rose-900/80',
    Icon: AlertCircle,
  },
};

const SIZE_STYLES = {
  sm: {
    padding: 'pl-3 pr-3.5 py-2',
    accent: 'w-0.5',
    iconWrap: 'w-5 h-5',
    iconSize: 'w-3 h-3',
    text: 'text-xs leading-relaxed',
    gap: 'gap-2',
  },
  md: {
    padding: 'pl-3.5 pr-4 py-3',
    accent: 'w-1',
    iconWrap: 'w-7 h-7',
    iconSize: 'w-4 h-4',
    text: 'text-sm leading-relaxed',
    gap: 'gap-3',
  },
};

export default function HelperNote({
  children,
  variant = 'info',
  size = 'sm',
  icon: IconOverride,
  className = '',
  testId,
}) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.info;
  const s = SIZE_STYLES[size] || SIZE_STYLES.sm;
  const Icon = IconOverride || v.Icon;

  return (
    <div
      data-testid={testId}
      className={`relative flex items-start ${s.gap} ${s.padding} rounded-lg border ${v.container} ring-1 ring-inset ${className}`}
    >
      <span className={`absolute left-0 top-2 bottom-2 ${s.accent} rounded-r-full ${v.accent}`} aria-hidden="true" />
      <span className={`flex-shrink-0 ${s.iconWrap} rounded-full ${v.iconBg} ${v.iconColor} flex items-center justify-center mt-0.5`}>
        <Icon className={s.iconSize} />
      </span>
      <div className={`flex-1 ${s.text} ${v.text}`}>{children}</div>
    </div>
  );
}
